import { BasisSwitcher } from "@/components/accounting/basis-switcher";
import { PeriodSelector } from "@/components/accounting/period-selector";
import { ReportLinesTable } from "@/components/accounting/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import type {
	ConsolidationResult,
	IcTransaction,
} from "@/gen/omnios/v1/accounting_pb";
import { PeriodStatus } from "@/gen/omnios/v1/accounting_pb";
import {
	useListPeriods,
	useRunConsolidation,
} from "@/hooks/use-accounting";
import { useOrganizationStore } from "@/lib/organization";
import { useWorkspaceStore } from "@/lib/workspace";

import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { useGridLayout } from "@/lib/use-grid-layout";
import { useServerGrid } from "@/lib/use-server-grid";
import { accountingClient } from "@/lib/accounting-client";
import type { ColDef, ValueFormatterParams } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronDown, Loader2, Search } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/accounting/consolidation")({
	component: ConsolidationPage,
});

function currentPeriodId(): string {
	const now = new Date();
	const y = now.getFullYear();
	const m = String(now.getMonth() + 1).padStart(2, "0");
	return `${y}-${m}`;
}

function formatAmount(value: number): string {
	return new Intl.NumberFormat("ru-RU", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value);
}

const IC_STATUS_COLORS: Record<string, string> = {
	pending: "hsl(var(--muted-foreground))",
	matched: "#ca8a04",
	eliminated: "hsl(var(--chart-2))",
};

function ConsolidationPage() {
	const { t } = useTranslation("accounting");
	useBreadcrumbs([{ label: t("nav_consolidation") }]);
	const { onGridReady, onStateChanged } = useGridLayout("consolidation");

	// Org entities
	const selectedOrgId = useOrganizationStore((s) => s.selectedOrgId);
	const workspaces = useWorkspaceStore((s) => s.workspaces);
	const orgEntities = useMemo(
		() =>
			selectedOrgId
				? workspaces.filter((w) => w.organizationId === selectedOrgId)
				: workspaces,
		[workspaces, selectedOrgId],
	);

	const [selectedEntityIds, setSelectedEntityIds] = useState<Set<string>>(
		() => new Set(orgEntities.map((e) => e.id)),
	);

	const { data: periodsData } = useListPeriods();
	const periods = useMemo(
		() =>
			(periodsData?.periods ?? []).map((p) => ({
				id: p.periodId,
				label: p.periodId,
				status:
					p.status === PeriodStatus.OPEN
						? "open"
						: p.status === PeriodStatus.CLOSED
							? "closed"
							: "soft-closed",
			})),
		[periodsData],
	);
	const [periodId, setPeriodId] = useState(currentPeriodId);
	const [basis, setBasis] = useState("accrual");
	const [eliminateIc, setEliminateIc] = useState(true);
	const [resultTab, setResultTab] = useState("ic");

	const consolidation = useRunConsolidation();
	const result: ConsolidationResult | undefined =
		consolidation.data?.result ?? undefined;
	const runStatus = consolidation.data?.status;

	const { gridRef, gridProps, totalRows: icTotalRows, refreshGrid } = useServerGrid<IcTransaction>({
		colToField: {},
		fetchRows: async ({ pageSize, pageToken, sort, filter }) => {
			const resp = await accountingClient.listIcTransactions({
				page: { pageSize, pageToken },
				sort: sort ? { field: sort.field, descending: sort.descending } : undefined,
				filter: filter as never,
			});
			return {
				rows: resp.transactions,
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
	});

	const entityName = useCallback(
		(id: string): string =>
			workspaces.find((w) => w.id === id)?.name ?? id.slice(0, 8),
		[workspaces],
	);

	function toggleEntity(id: string) {
		setSelectedEntityIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}

	function toggleAll() {
		if (selectedEntityIds.size === orgEntities.length) {
			setSelectedEntityIds(new Set());
		} else {
			setSelectedEntityIds(new Set(orgEntities.map((e) => e.id)));
		}
	}

	function handleRun() {
		consolidation.mutate({
			periodId,
			entityIds: Array.from(selectedEntityIds),
			basis,
			eliminateIc,
		});
	}

	// ── IC Transaction AG Grid column defs ──
	const icColumnDefs = useMemo<ColDef<IcTransaction>[]>(
		() => [
			{
				field: "fromEntityId",
				headerName: t("ic_from"),
				width: 160,
				valueFormatter: (p: ValueFormatterParams<IcTransaction>) =>
					entityName(p.value ?? ""),
			},
			{
				field: "toEntityId",
				headerName: t("ic_to"),
				width: 160,
				valueFormatter: (p: ValueFormatterParams<IcTransaction>) =>
					entityName(p.value ?? ""),
			},
			{
				field: "amount",
				headerName: t("line_amount"),
				width: 140,
				type: "rightAligned",
				filter: "agNumberColumnFilter",
				cellClass: "font-tabular",
				valueGetter: (p) => Number.parseFloat(p.data?.amount || "0"),
				valueFormatter: (p: ValueFormatterParams) => formatAmount(p.value ?? 0),
			},
			{
				field: "currency",
				headerName: t("entry_currency"),
				width: 70,
				cellClass: "font-mono text-xs",
			},
			{
				field: "status",
				headerName: t("entry_status"),
				width: 120,
				filter: false,
				cellRenderer: (p: { value: string }) => {
					const color = IC_STATUS_COLORS[p.value] ?? "hsl(var(--muted-foreground))";
					return <span style={{ color }}>{t(`ic_status_${p.value}`)}</span>;
				},
			},
		],
		[t, entityName],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "period",
				content: `${t("period")}: ${periodId}`,
				align: "left" as const,
			},
			{
				id: "basis",
				content: `${t("basis")}: ${basis.toUpperCase()}`,
				align: "left" as const,
			},
			{
				id: "count",
				content: t("row_count", { count: icTotalRows }),
				align: "left" as const,
			},
		],
		[periodId, basis, icTotalRows, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption(
								"quickFilterText",
								e.target.value,
							)
						}
						className="pl-9 h-9"
					/>
				</div>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="outline" size="sm" className="h-9">
							{t("toolbar_export")}
							<ChevronDown className="h-3.5 w-3.5 ml-1 opacity-50" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem
							onClick={() => gridRef.current?.api?.exportDataAsCsv()}
						>
							CSV
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
				<Button
					size="sm"
					className="h-9"
					onClick={handleRun}
					disabled={consolidation.isPending || selectedEntityIds.size === 0}
				>
					{t("run_consolidation")}
				</Button>
			</div>
			{/* Controls bar */}
			<div className="flex flex-wrap items-center gap-3 px-2 py-1.5 border-b bg-muted/20 text-xs">
				{/* Entity checkboxes */}
				<div className="flex items-center gap-2">
					<Checkbox
						id="select-all"
						checked={selectedEntityIds.size === orgEntities.length}
						onCheckedChange={toggleAll}
					/>
					<Label htmlFor="select-all" className="text-xs font-medium">
						{t("report_all_entities")}
					</Label>
				</div>
				{orgEntities.map((entity) => (
					<div key={entity.id} className="flex items-center gap-1">
						<Checkbox
							id={`entity-${entity.id}`}
							checked={selectedEntityIds.has(entity.id)}
							onCheckedChange={() => toggleEntity(entity.id)}
						/>
						<Label htmlFor={`entity-${entity.id}`} className="text-xs">
							{entity.name}
						</Label>
					</div>
				))}

				<div className="h-4 w-px bg-border mx-1" />

				<PeriodSelector
					value={periodId}
					onChange={setPeriodId}
					periods={periods}
				/>
				<BasisSwitcher value={basis} onChange={setBasis} />

				<div className="h-4 w-px bg-border mx-1" />

				<div className="flex items-center gap-1">
					<Switch
						id="eliminate-ic"
						checked={eliminateIc}
						onCheckedChange={setEliminateIc}
					/>
					<Label htmlFor="eliminate-ic" className="text-xs">
						{t("eliminate_ic")}
					</Label>
				</div>
			</div>

			{/* Consolidation result tabs */}
			{runStatus && (
				<div className="flex items-center border-b shrink-0">
					<div className="flex items-center gap-2 px-3 py-1.5 text-xs">
						{runStatus === "running" && (
							<Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
						)}
						{runStatus === "completed" && (
							<Badge variant="default" className="text-[10px]">
								{t("consolidation_completed")}
							</Badge>
						)}
						{runStatus === "failed" && (
							<Badge variant="destructive" className="text-[10px]">
								{t("consolidation_failed")}
							</Badge>
						)}
						{result && (
							<Badge variant="outline" className="text-[10px]">
								{t("eliminations_count", { count: result.eliminationsCount })}
							</Badge>
						)}
					</div>
					<div className="flex-1" />
					{result && (
						<div className="flex items-center pr-2">
							{[
								{ id: "ic", label: t("ic_transactions") },
								...(result.consolidatedIncomeStatement
									? [{ id: "pnl", label: t("income_statement") }]
									: []),
								...(result.consolidatedBalanceSheet
									? [{ id: "bs", label: t("balance_sheet") }]
									: []),
							].map((tab) => (
								<Button
									key={tab.id}
									variant="ghost"
									size="sm"
									onClick={() => setResultTab(tab.id)}
									className={`px-2.5 py-1 text-xs rounded-none border-b-2 ${
										resultTab === tab.id
											? "border-primary text-foreground font-medium"
											: "border-transparent text-muted-foreground hover:text-foreground"
									}`}
								>
									{tab.label}
								</Button>
							))}
						</div>
					)}
				</div>
			)}

			{/* Results content */}
			<div className="flex-1 min-h-0 overflow-auto">
				{resultTab === "pnl" && result?.consolidatedIncomeStatement ? (
					<ReportLinesTable
						lines={result.consolidatedIncomeStatement.lines}
						total={result.consolidatedIncomeStatement.total}
						title={
							result.consolidatedIncomeStatement.title || t("income_statement")
						}
					/>
				) : resultTab === "bs" && result?.consolidatedBalanceSheet ? (
					<ReportLinesTable
						lines={result.consolidatedBalanceSheet.lines}
						total={result.consolidatedBalanceSheet.total}
						title={result.consolidatedBalanceSheet.title || t("balance_sheet")}
					/>
				) : (
					<div className="h-full ag-theme-quartz" style={{ width: "100%" }}>
						<AgGridReact<IcTransaction>
							ref={gridRef}
							{...gridProps}
							columnDefs={icColumnDefs}
							defaultColDef={defaultColDef}
							localeText={AG_GRID_LOCALE_RU}
							onGridReady={onGridReady}
							onSortChanged={() => {
								onStateChanged();
								refreshGrid();
							}}
							onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
							onColumnMoved={onStateChanged}
							onColumnResized={onStateChanged}
							onColumnVisible={onStateChanged}
							rowHeight={28}
							headerHeight={32}
							animateRows={false}
							getRowId={(p) => p.data.id}
							overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("empty_ic_title", { defaultValue: "Нет IC транзакций" })}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("empty_ic_desc", { defaultValue: "Запустите консолидацию, чтобы обнаружить межфирменные операции" })}</p></div>`}
						/>
					</div>
				)}
			</div>
		</div>
	);
}
